import { Button, CheckCircle, Dialog, DialogButtons, Modal } from '@st/theme'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { SendClientOrganizerDialogContent } from '../../send-client-organizer-dialog'
import { selDeliveryWizard, type DeliveryWizardMessage } from './delivery-wizard'
import { InvoiceLinkFields } from './invoice-link'

export function DeliveryWizardModal() {
  const folder = useProcess('stFolder')
  const route = useProcess('route')
  const model = useSelect('stFolder', (state) =>
    selDeliveryWizard(state.deliveryWizard, state.organizerWrites)
  )
  const send = (message: DeliveryWizardMessage) => folder.send({ type: 'deliveryWizard', message })
  const onClose = () => send({ type: 'closed' })
  if (model.kind == 'closed') return null
  return (
    <Modal isOpen={true}>
      {match(model)
        .with({ kind: 'deliveryInvoice' }, (model) => (
          <Dialog
            title="Add an invoice link (optional)"
            subtitle="Adding an invoice link adds a step for the client to pay their invoice. Leave it blank to skip this step."
            className="w-full max-w-2xl"
            buttons={
              <DialogButtons>
                <Button
                  variant="subtle"
                  disabled={model.busy}
                  onClick={() => send({ type: 'stepChanged', step: 'email' })}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  disabled={!model.canSend}
                  onClick={() => send({ type: 'sendRequested' })}
                >
                  {model.sendLabel}
                </Button>
              </DialogButtons>
            }
          >
            <InvoiceLinkFields
              draft={model.invoice}
              onEdit={(edit) => send({ type: 'invoiceEdited', edit })}
            />
          </Dialog>
        ))
        .with({ kind: 'deliverySent' }, ({ clientName }) => (
          <Dialog
            title={
              <span className="flex items-center gap-2">
                <CheckCircle className="size-7 text-green-600" />
                Return has been delivered
              </span>
            }
            subtitle="Your client has received their tax return, and will be guided to review and sign it."
            className="w-full max-w-2xl"
            buttons={
              <DialogButtons>
                <Button variant="subtle" onClick={onClose}>
                  Not now
                </Button>
                <Button
                  variant="primary"
                  onClick={() => folder.send({ type: 'deliveryChecklistRequested' })}
                >
                  Create checklist
                </Button>
              </DialogButtons>
            }
          >
            <div className="rounded-md border border-gray-200 px-5 py-4">
              <div className="font-semibold text-gray-900">
                Create a tax payment checklist for {clientName}
              </div>
              <p className="mt-1 text-sm text-gray-700">
                Tell {clientName} exactly which tax payments they need to make and how to make them.
              </p>
            </div>
          </Dialog>
        ))
        .otherwise((model) => (
          <SendClientOrganizerDialogContent
            model={model}
            send={(message) => send({ type: 'email', message })}
            onSend={() => send({ type: 'stepChanged', step: 'invoice' })}
            onClose={onClose}
            onPreview={(url) => route.send({ type: 'openUrl', url })}
          />
        ))}
    </Modal>
  )
}
